import React from 'react';
import { Link } from 'react-router-dom';
import { Bot, Globe, Megaphone, LineChart, GraduationCap, Scale, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

type ServiceId = 'ai' | 'web' | 'social' | 'seo' | 'training' | 'dpo';

interface ServiceCrossSellProps {
  current: ServiceId;
  eyebrow?: string;
  heading?: string;
}

const ALL = [
  { id: 'ai',       to: '/services/ai',       Icon: Bot,            accent: true },
  { id: 'web',      to: '/services/web',      Icon: Globe },
  { id: 'social',   to: '/services/social',   Icon: Megaphone },
  { id: 'seo',      to: '/services/seo',      Icon: LineChart },
  { id: 'training', to: '/services/training', Icon: GraduationCap },
  { id: 'dpo',      to: '/services/dpo',      Icon: Scale },
] as const;

const ServiceCrossSell: React.FC<ServiceCrossSellProps> = ({ current, eyebrow, heading }) => {
  const { t, language } = useLanguage();
  const isPT = language === 'pt';
  const items = ALL.filter(s => s.id !== current);

  return (
    <section className="relative py-20 md:py-24 bg-background" aria-labelledby="cross-sell-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-3xl">
          <span className="eyebrow">— {eyebrow ?? (isPT ? 'Explorar mais' : 'Explore more')}</span>
          <h2
            id="cross-sell-heading"
            className="mt-4 font-display text-foreground text-[clamp(1.75rem,3.6vw,3rem)] leading-[1.05] tracking-tight"
          >
            {heading ?? (isPT ? 'Outros serviços do estúdio.' : 'Other studio services.')}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {items.map(({ id, to, Icon, accent }) => (
            <Link
              key={id}
              to={to}
              className={[
                'group relative overflow-hidden rounded-2xl border p-7 transition-all duration-500 hover:-translate-y-1',
                accent
                  ? 'border-lime/30 bg-card hover:border-lime/50'
                  : 'border-border/80 bg-card/60 hover:bg-card hover:border-foreground/30',
              ].join(' ')}
            >
              <div
                className={[
                  'pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100',
                  accent ? 'bg-lime/[0.10]' : 'bg-primary/[0.10]',
                ].join(' ')}
                aria-hidden="true"
              />

              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div
                    className={[
                      'flex h-11 w-11 items-center justify-center rounded-xl border transition-colors',
                      accent
                        ? 'border-lime/30 bg-lime/[0.08] text-lime group-hover:bg-lime/[0.15]'
                        : 'border-border bg-secondary text-foreground',
                    ].join(' ')}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.6} />
                  </div>
                  <ArrowUpRight
                    className={[
                      'h-4 w-4 transition-all duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5',
                      accent ? 'text-lime' : 'text-muted-foreground group-hover:text-foreground',
                    ].join(' ')}
                  />
                </div>
                <h3 className="font-display text-2xl md:text-[1.5rem] text-foreground leading-tight tracking-tight mb-2">
                  {t(`nav.${id}`)}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(`services.${id}.oneLiner`)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceCrossSell;
